// Imports
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ContainerCard, ModuleTitleCard } from "../../components/ui/cards";
import { Reactable } from "../../components/ui/reactable";
import { EMPLOYEE_SHIFT } from "../../constants/api";
import { checkUserPermissionFunc, truncateText } from "../../components/common/CommonFunctions";
import { Clock, Edit } from "lucide-react";
import { Button } from "../../components/ui/button";
import dayjs from "dayjs";

import duration from "dayjs/plugin/duration";

// extend the plugin
dayjs.extend(duration);

const EmployeeShift = () => {
    // States
    const [employeeShiftData, setEmployeeShiftData] = useState([]);
    const [totalRecords, setTotalRecords] = useState(0);

    const navigate = useNavigate();

    // Permissions
    const {
        add: addPermission,
        edit: editPermission,
        draft: draftPermission,
        active: isActive
    } = checkUserPermissionFunc("configuration", "Shift Configuration", "SubMenu");

    // Functions
    const handleAddShift = () => {
        navigate("/configuration/shiftconfiguration/add");
    };

    // Columns
    const employeeShiftColumns = [
        {
            accessor: "code",
            Header: "Shift Code",
            sortable: true,
            filterable: true,
            width: '100px',
            Cell: ({ value }) => value || "-",
        },
        {
            accessor: "shift_name",
            Header: "Shift Name",
            sortable: true,
            filterable: true,
            width: "120px",
            Cell: ({ value }) => value || "-",
        },
        {
            accessor: "shift_timings",
            Header: "Shift Timings",
            sortable: true,
            Cell: ({ row }) => {
                const from = row?.shift_timings_from;
                const to = row?.shift_timings_to;
                return from && to ? (
                    <div className="text-xs font-medium text-gray-700 dark:text-gray-200">
                        {from} <span className="text-gray-400">–</span> {to}
                    </div>
                ) : (
                    "-"
                );
            },
        },
        {
            accessor: "shift_hours",
            Header: "Shift Hours Summary",
            sortable: false,
            Cell: ({ row }) => {
                const breakHrs = row?.total_break_hrs;
                const shiftHrs = row?.total_shift_hrs;

                // helper function to format HH:mm:ss into "HH hrs mm mins"
                const formatDuration = (timeStr) => {
                    if (!timeStr) return "-";
                    const [hours, minutes, seconds] = timeStr.split(":").map(Number);
                    const dur = dayjs.duration({
                        hours: hours || 0,
                        minutes: minutes || 0,
                        seconds: seconds || 0,
                    });

                    // if you prefer "08:30 hrs" instead of "08 hrs 30 mins"
                    return `${String(dur.hours()).padStart(2, "0")}:${String(
                        dur.minutes()
                    ).padStart(2, "0")} hrs`;
                };

                return (
                    <div className="text-xs text-gray-600 dark:text-gray-300">
                        <span className="font-medium text-gray-700 dark:text-gray-100">
                            {shiftHrs ? formatDuration(shiftHrs) : "-"}
                        </span>
                        {breakHrs && (
                            <span className="text-[11px] text-gray-500 dark:text-gray-400 ml-1">
                                (Break: {formatDuration(breakHrs)})
                            </span>
                        )}
                    </div>
                );
            },
        },
        {
            accessor: "notes",
            Header: "Notes",
            sortable: true,
            Cell: ({ value }) => value ? truncateText(value, 20, "300px", "balance") : "-"
        },
        {
            accessor: "is_active",
            Header: "Status",
            sortable: true,
            Cell: ({ value }) => (
                <span
                    className={`px-2 py-[2px] text-xs font-medium rounded-full ${value
                        ? "bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300"
                        : "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300"
                        }`}
                >
                    {value ? "Active" : "Inactive"}
                </span>
            ),
        },
        ...(editPermission
            ? [
                {
                    accessor: "action",
                    Header: "Action",
                    sortable: false,
                    Cell: ({ row }) => {
                        const handleOpenEdit = () => {
                            navigate("/configuration/shiftconfiguration/add", {
                                state: { fromEdit: true, editDetails: row, isActive: isActive },
                            });
                        };

                        return (
                            <div className="flex items-center gap-2">
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    className="text-sidebar-primary hover:bg-blue-50 dark:hover:bg-gray-700"
                                    onClick={handleOpenEdit}
                                >
                                    <Edit className="h-4 w-4" />
                                </Button>
                            </div>
                        );
                    },
                },
            ]
            : []),
    ];


    // UI
    return (
        <ContainerCard>
            <ModuleTitleCard
                Icon={Clock}
                title="Employee Shift"
                subtitle="Manage and configure employee work shifts"
                totalRecords={totalRecords}
                buttonEnable={!!addPermission}
                setOpenDialog={handleAddShift}
                buttonName="Add Shift"
            />

            <Reactable
                columns={employeeShiftColumns}
                data={employeeShiftData}
                setData={setEmployeeShiftData}
                setTotalCount={setTotalRecords}
                url={EMPLOYEE_SHIFT}
                serverSide={true}
                tableHeight="calc(100vh - 320px)"
                rowSize={editPermission ? "sm" : null}
                isDraftRequired={!!draftPermission}
                isCSVRequired={true}
                isPDFRequired={true}
            />
        </ContainerCard>
    );
};

export default EmployeeShift;
